import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Link, Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { formatDate } from 'utils/date';
import {
  ELicenceAuthorityPrintoutErrorLogStatus,
  ELicenceAuthorityPrintoutErrorLogType,
} from 'models/licenceAuthority';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { EIconTypes } from 'constants/Icons';
import { emptyFieldHolder } from 'components/molecules/CardList';

interface ILicenceAuthorityPrintoutErrorLogTableCard {
  data: ITableDataSource;
  longDateFormat: string;
  resolveAction: (uniqueId: string) => Promise<void>;
}

export const LicenceAuthorityPrintoutErrorLogTableCard = ({
  data,
  longDateFormat,
  resolveAction,
}: ILicenceAuthorityPrintoutErrorLogTableCard) => {
  const navigate = useNavigate();
  const { t } = useTranslations();

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
        paddingBottom: 2,
      }}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.type.column')}
        </Typography>
        <Typography variant="body2">{data.type}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.messageKey.column')}
        </Typography>
        <Typography variant="body2">{t(data.messageKey)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.batchName.column')}
        </Typography>
        <Typography variant="body2">
          {data.batchUniqueId ? (
            <Link
              href={ERouteLinks.PrintoutBatchDetails.replace(':id', data.batchUniqueId)}
              underline="none"
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.batchName}
            </Link>
          ) : (
            emptyFieldHolder(data.batchName)
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.detail.column')}
        </Typography>
        <Typography variant="body2">{data.detail}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.status.column')}
        </Typography>
        {data.status === ELicenceAuthorityPrintoutErrorLogStatus.ACTIVE ? (
          <Chips
            text={t('licenceAuthorities.printoutErrorLog.table.header.status.active.column')}
            variant={EChipsVariants.error}
            fullWidth
          />
        ) : (
          <Chips
            text={t('licenceAuthorities.printoutErrorLog.table.header.status.resolved.column')}
            variant={EChipsVariants.success}
            fullWidth
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.firstSeen.column')}
        </Typography>
        <Typography variant="body2">{formatDate(data.firstSeen, longDateFormat)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.lastSeen.column')}
        </Typography>
        <Typography variant="body2">{formatDate(data.lastSeen, longDateFormat)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.printoutErrorLog.table.header.occurence.column')}
        </Typography>
        <Typography variant="body2">{data.occurence}</Typography>
      </Grid>
      {data.type === ELicenceAuthorityPrintoutErrorLogType.MANIFEST_REJECTED &&
        data.status === ELicenceAuthorityPrintoutErrorLogStatus.ACTIVE && (
          <Grid
            size={{ xs: 12 }}
            sx={{ px: 2, pt: 2, display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('general.actions.button')}
            </Typography>
            <Button
              id={`licenceAuthorities_view-button-${data.id}`}
              variant={EButtonVariants.outlined}
              size={EButtonSizes.small}
              label={t('licenceAuthorities.printoutErrorLog.table.header.resolve.button')}
              onClick={() => resolveAction(data.id as string)}
            />
          </Grid>
        )}
    </Grid>
  );
};
